import React from 'react';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { ArrowLeft, CheckCircle, Settings, Globe, Palette } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
export function AgencyWhiteLabelSetupPage() {
  const navigate = useNavigate();
  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/resources');
    }
  };
  return <div className="bg-white" data-id="element-819">
      {/* Header */}
      <div className="bg-gray-900 py-12 lg:py-16" data-id="element-820">
        <Container data-id="element-821">
          <button onClick={handleGoBack} className="flex items-center text-gray-300 hover:text-white mb-6 transition-colors" data-id="element-822">
            <ArrowLeft className="h-5 w-5 mr-2" data-id="element-823" />
            Back
          </button>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-4" data-id="element-824">
            Agency White-Label Setup Guide
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl" data-id="element-825">
            Complete guide to setting up custom domains, branded reports, and
            white-label features for your agency clients.
          </p>
        </Container>
      </div>

      {/* Content */}
      <article className="py-16 lg:py-24" data-id="element-826">
        <Container size="md" data-id="element-827">
          {/* Featured Image */}
          <div className="mb-12" data-id="element-828">
            <img src="https://cdn.magicpatterns.com/uploads/bNspXKD9SxdNKRzSMSk5Le/4._white-label.png" alt="White-Label Branding Settings" className="w-full rounded-lg shadow-xl" data-id="element-829" />
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12" data-id="element-830">
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-831">
              Flowsense's white-label features allow agencies to deliver
              professional, branded reports and experiences to clients. This
              guide covers everything from custom domains to branded exports.
            </p>
          </div>

          {/* Why White-Label */}
          <section className="mb-16" data-id="element-832">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-833">
              Why White-Label Matters for Agencies
            </h2>
            <p className="text-lg text-gray-600 mb-6" data-id="element-834">
              White-labeling Flowsense helps you:
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-835">
              <div className="bg-indigo-50 p-6 rounded-lg" data-id="element-836">
                <h3 className="text-lg font-semibold text-indigo-900 mb-3" data-id="element-837">
                  Build Your Brand
                </h3>
                <p className="text-indigo-700" data-id="element-838">
                  Present Flowsense as your proprietary technology,
                  strengthening your agency's value proposition and competitive
                  positioning.
                </p>
              </div>
              <div className="bg-purple-50 p-6 rounded-lg" data-id="element-839">
                <h3 className="text-lg font-semibold text-purple-900 mb-3" data-id="element-840">
                  Professional Deliverables
                </h3>
                <p className="text-purple-700" data-id="element-841">
                  Send clients branded reports with your logo, colors, and
                  domain—no mention of third-party tools.
                </p>
              </div>
              <div className="bg-pink-50 p-6 rounded-lg" data-id="element-842">
                <h3 className="text-lg font-semibold text-pink-900 mb-3" data-id="element-843">
                  Increase Perceived Value
                </h3>
                <p className="text-pink-700" data-id="element-844">
                  Clients see a cohesive, professional experience that
                  reinforces your agency's expertise and attention to detail.
                </p>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg" data-id="element-845">
                <h3 className="text-lg font-semibold text-blue-900 mb-3" data-id="element-846">
                  Streamline Operations
                </h3>
                <p className="text-blue-700" data-id="element-847">
                  Manage all clients from one dashboard while each client sees
                  only their branded experience.
                </p>
              </div>
            </div>
          </section>

          {/* Setup Steps */}
          <section className="mb-16" data-id="element-848">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-849">
              White-Label Setup: Step-by-Step
            </h2>

            <div className="space-y-8" data-id="element-850">
              {/* Step 1: Logo Upload */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-8 rounded-lg" data-id="element-851">
                <div className="flex items-start mb-4" data-id="element-852">
                  <span className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-853">
                    1
                  </span>
                  <div className="flex-grow" data-id="element-854">
                    <div className="flex items-center mb-3" data-id="element-855">
                      <Palette className="h-6 w-6 text-indigo-600 mr-2" data-id="element-856" />
                      <h3 className="text-xl font-semibold text-gray-900" data-id="element-857">
                        Upload Your Agency Logo
                      </h3>
                    </div>
                    <p className="text-gray-700 mb-4" data-id="element-858">
                      Navigate to <strong data-id="element-859">Settings → Branding → Logo</strong>{' '}
                      and upload your agency logo.
                    </p>
                    <div className="bg-white p-4 rounded border border-indigo-200 mb-4" data-id="element-860">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-861">
                        Logo Requirements:
                      </h4>
                      <ul className="space-y-2 text-gray-700 text-sm" data-id="element-862">
                        <li className="flex items-start" data-id="element-863">
                          <CheckCircle className="h-4 w-4 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-864" />
                          <span data-id="element-865">
                            <strong data-id="element-866">Format:</strong> PNG, JPG, or SVG
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-867">
                          <CheckCircle className="h-4 w-4 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-868" />
                          <span data-id="element-869">
                            <strong data-id="element-870">Size:</strong> Maximum 5MB
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-871">
                          <CheckCircle className="h-4 w-4 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-872" />
                          <span data-id="element-873">
                            <strong data-id="element-874">Dimensions:</strong> Recommended 400x100px
                            (4:1 ratio)
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-875">
                          <CheckCircle className="h-4 w-4 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-876" />
                          <span data-id="element-877">
                            <strong data-id="element-878">Background:</strong> Transparent PNG works
                            best
                          </span>
                        </li>
                      </ul>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-879">
                      Your logo will appear in client reports, exported files,
                      and on your custom domain (if configured).
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 2: Brand Color */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-lg" data-id="element-880">
                <div className="flex items-start mb-4" data-id="element-881">
                  <span className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-882">
                    2
                  </span>
                  <div className="flex-grow" data-id="element-883">
                    <div className="flex items-center mb-3" data-id="element-884">
                      <Palette className="h-6 w-6 text-purple-600 mr-2" data-id="element-885" />
                      <h3 className="text-xl font-semibold text-gray-900" data-id="element-886">
                        Set Your Brand Color
                      </h3>
                    </div>
                    <p className="text-gray-700 mb-4" data-id="element-887">
                      Choose your primary brand color in{' '}
                      <strong data-id="element-888">Settings → Branding → Brand Color</strong>.
                    </p>
                    <div className="bg-white p-4 rounded border border-purple-200 mb-4" data-id="element-889">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-890">
                        Color Usage:
                      </h4>
                      <ul className="space-y-2 text-gray-700 text-sm" data-id="element-891">
                        <li className="flex items-start" data-id="element-892">
                          <CheckCircle className="h-4 w-4 text-purple-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-893" />
                          <span data-id="element-894">Report headers and accents</span>
                        </li>
                        <li className="flex items-start" data-id="element-895">
                          <CheckCircle className="h-4 w-4 text-purple-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-896" />
                          <span data-id="element-897">Charts and data visualizations</span>
                        </li>
                        <li className="flex items-start" data-id="element-898">
                          <CheckCircle className="h-4 w-4 text-purple-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-899" />
                          <span data-id="element-900">Buttons and interactive elements</span>
                        </li>
                        <li className="flex items-start" data-id="element-901">
                          <CheckCircle className="h-4 w-4 text-purple-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-902" />
                          <span data-id="element-903">Exported PDF styling</span>
                        </li>
                      </ul>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-904">
                      Enter your brand's hex color code (e.g., #6366F1). The
                      color picker helps you match your exact brand color.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3: Report Footer */}
              <div className="bg-gradient-to-r from-pink-50 to-red-50 p-8 rounded-lg" data-id="element-905">
                <div className="flex items-start mb-4" data-id="element-906">
                  <span className="flex-shrink-0 w-10 h-10 bg-pink-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-907">
                    3
                  </span>
                  <div className="flex-grow" data-id="element-908">
                    <div className="flex items-center mb-3" data-id="element-909">
                      <Settings className="h-6 w-6 text-pink-600 mr-2" data-id="element-910" />
                      <h3 className="text-xl font-semibold text-gray-900" data-id="element-911">
                        Customize Report Footer
                      </h3>
                    </div>
                    <p className="text-gray-700 mb-4" data-id="element-912">
                      Add custom footer text in{' '}
                      <strong data-id="element-913">Settings → Branding → Report Footer</strong>.
                    </p>
                    <div className="bg-white p-4 rounded border border-pink-200 mb-4" data-id="element-914">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-915">
                        Footer Examples:
                      </h4>
                      <ul className="space-y-3 text-gray-700 text-sm" data-id="element-916">
                        <li className="bg-gray-50 p-3 rounded" data-id="element-917">
                          <strong data-id="element-918">Simple:</strong> "Powered by [Your Agency
                          Name]"
                        </li>
                        <li className="bg-gray-50 p-3 rounded" data-id="element-919">
                          <strong data-id="element-920">With Contact:</strong> "Generated by [Your
                          Agency] | contact@youragency.com"
                        </li>
                        <li className="bg-gray-50 p-3 rounded" data-id="element-921">
                          <strong data-id="element-922">With Website:</strong> "Powered by [Your
                          Agency] | www.youragency.com"
                        </li>
                      </ul>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-923">
                      This text appears at the bottom of all exported reports
                      and client-facing documents.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 4: Custom Domain */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-lg" data-id="element-924">
                <div className="flex items-start mb-4" data-id="element-925">
                  <span className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-4" data-id="element-926">
                    4
                  </span>
                  <div className="flex-grow" data-id="element-927">
                    <div className="flex items-center mb-3" data-id="element-928">
                      <Globe className="h-6 w-6 text-blue-600 mr-2" data-id="element-929" />
                      <h3 className="text-xl font-semibold text-gray-900" data-id="element-930">
                        Set Up Custom Domain (Optional)
                      </h3>
                    </div>
                    <p className="text-gray-700 mb-4" data-id="element-931">
                      Connect your own domain in{' '}
                      <strong data-id="element-932">Settings → Branding → Custom Domain</strong>.
                    </p>

                    <div className="bg-white p-4 rounded border border-blue-200 mb-4" data-id="element-933">
                      <h4 className="font-semibold text-gray-900 mb-3" data-id="element-934">
                        Domain Setup Process:
                      </h4>
                      <ol className="space-y-3 text-gray-700 text-sm" data-id="element-935">
                        <li className="flex items-start" data-id="element-936">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5" data-id="element-937">
                            1
                          </span>
                          <div data-id="element-938">
                            <strong data-id="element-939">Choose Your Subdomain:</strong> Pick a
                            subdomain like flows.youragency.com or
                            insights.youragency.com
                          </div>
                        </li>
                        <li className="flex items-start" data-id="element-940">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5" data-id="element-941">
                            2
                          </span>
                          <div data-id="element-942">
                            <strong data-id="element-943">Enter in Flowsense:</strong> Add your
                            desired subdomain in the Custom Domain field
                          </div>
                        </li>
                        <li className="flex items-start" data-id="element-944">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5" data-id="element-945">
                            3
                          </span>
                          <div data-id="element-946">
                            <strong data-id="element-947">Get DNS Records:</strong> Flowsense will
                            provide CNAME records to add to your DNS
                          </div>
                        </li>
                        <li className="flex items-start" data-id="element-948">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5" data-id="element-949">
                            4
                          </span>
                          <div data-id="element-950">
                            <strong data-id="element-951">Update DNS:</strong> Add the CNAME records
                            in your domain registrar (GoDaddy, Cloudflare, etc.)
                          </div>
                        </li>
                        <li className="flex items-start" data-id="element-952">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xs mr-3 mt-0.5" data-id="element-953">
                            5
                          </span>
                          <div data-id="element-954">
                            <strong data-id="element-955">Verify & Activate:</strong> Click "Verify
                            Domain" in Flowsense (DNS propagation takes 24-48
                            hours)
                          </div>
                        </li>
                      </ol>
                    </div>

                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4" data-id="element-956">
                      <p className="text-sm text-yellow-800" data-id="element-957">
                        <strong data-id="element-958">Note:</strong> Custom domains are available on
                        Agency plans. SSL certificates are automatically
                        provisioned and renewed.
                      </p>
                    </div>

                    <p className="text-sm text-gray-600" data-id="element-959">
                      Once configured, clients will access Flowsense through
                      your branded domain instead of useflowsense.com.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Client Management */}
          <section className="mb-16" data-id="element-960">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-961">
              Managing Multiple Clients
            </h2>
            <p className="text-lg text-gray-600 mb-6" data-id="element-962">
              Flowsense's agency dashboard makes it easy to manage unlimited
              client accounts:
            </p>

            <div className="bg-gray-50 p-8 rounded-lg mb-8" data-id="element-963">
              <h3 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-964">
                Client Isolation & Access Control
              </h3>
              <ul className="space-y-4" data-id="element-965">
                <li className="flex items-start" data-id="element-966">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-967" />
                  <div data-id="element-968">
                    <span className="font-semibold text-gray-900" data-id="element-969">
                      Complete Data Isolation:
                    </span>
                    <span className="text-gray-600" data-id="element-970">
                      {' '}
                      Each client's data is completely separate. Team members
                      can be granted access to specific clients only.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-971">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-972" />
                  <div data-id="element-973">
                    <span className="font-semibold text-gray-900" data-id="element-974">
                      Role-Based Permissions:
                    </span>
                    <span className="text-gray-600" data-id="element-975">
                      {' '}
                      Assign team members as Owner, Admin, Superuser, User,
                      Analyst, or Read-Only for each client.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-976">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-977" />
                  <div data-id="element-978">
                    <span className="font-semibold text-gray-900" data-id="element-979">
                      Separate Klaviyo Connections:
                    </span>
                    <span className="text-gray-600" data-id="element-980">
                      {' '}
                      Each client connects their own Klaviyo account via OAuth.
                      No shared credentials or data leakage.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-981">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-982" />
                  <div data-id="element-983">
                    <span className="font-semibold text-gray-900" data-id="element-984">
                      Client Dashboard View:
                    </span>
                    <span className="text-gray-600" data-id="element-985">
                      {' '}
                      See all clients at a glance with health scores, flow
                      counts, connection status, and top opportunities.
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-indigo-50 p-6 rounded-lg border-l-4 border-indigo-500" data-id="element-986">
              <h4 className="font-semibold text-indigo-900 mb-2" data-id="element-987">
                Pro Tip: Client Naming Convention
              </h4>
              <p className="text-indigo-800 text-sm" data-id="element-988">
                Use a consistent naming format for clients (e.g., "Client Name -
                Industry" or "ClientName_Prod") to keep your dashboard organized
                as you scale.
              </p>
            </div>
          </section>

          {/* Export & Reporting */}
          <section className="mb-16" data-id="element-989">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-990">
              Branded Exports & Reports
            </h2>
            <p className="text-lg text-gray-600 mb-6" data-id="element-991">
              All exports automatically include your white-label branding:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-992">
              <div className="bg-white border-2 border-gray-200 p-6 rounded-lg" data-id="element-993">
                <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-994">
                  CSV Exports
                </h3>
                <ul className="space-y-2 text-gray-600 text-sm" data-id="element-995">
                  <li data-id="element-996">• Custom file naming with your agency name</li>
                  <li data-id="element-997">• Branded header row with your colors</li>
                  <li data-id="element-998">• Footer with your agency attribution</li>
                  <li data-id="element-999">• Automatic date stamping</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 p-6 rounded-lg" data-id="element-1000">
                <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1001">
                  JSON Exports
                </h3>
                <ul className="space-y-2 text-gray-600 text-sm" data-id="element-1002">
                  <li data-id="element-1003">• Structured data with agency metadata</li>
                  <li data-id="element-1004">• Custom branding fields included</li>
                  <li data-id="element-1005">• Perfect for BI tool integration</li>
                  <li data-id="element-1006">• API-friendly format</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 p-6 rounded-lg" data-id="element-1007">
                <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1008">
                  PDF Reports (Coming Soon)
                </h3>
                <ul className="space-y-2 text-gray-600 text-sm" data-id="element-1009">
                  <li data-id="element-1010">• Full-page branded cover</li>
                  <li data-id="element-1011">• Your logo on every page</li>
                  <li data-id="element-1012">• Brand colors throughout</li>
                  <li data-id="element-1013">• Custom footer text</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 p-6 rounded-lg" data-id="element-1014">
                <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1015">
                  Shareable Links
                </h3>
                <ul className="space-y-2 text-gray-600 text-sm" data-id="element-1016">
                  <li data-id="element-1017">• Custom domain URLs</li>
                  <li data-id="element-1018">• Branded landing pages</li>
                  <li data-id="element-1019">• Password protection option</li>
                  <li data-id="element-1020">• Expiration dates for security</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Best Practices */}
          <section className="mb-16" data-id="element-1021">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1022">
              White-Label Best Practices
            </h2>
            <div className="bg-gray-50 p-8 rounded-lg" data-id="element-1023">
              <ul className="space-y-4" data-id="element-1024">
                <li className="flex items-start" data-id="element-1025">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1026" />
                  <div data-id="element-1027">
                    <span className="font-semibold text-gray-900" data-id="element-1028">
                      Set up branding before onboarding clients:
                    </span>
                    <span className="text-gray-600" data-id="element-1029">
                      {' '}
                      Configure your logo, colors, and domain before adding your
                      first client to ensure a consistent experience from day
                      one.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1030">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1031" />
                  <div data-id="element-1032">
                    <span className="font-semibold text-gray-900" data-id="element-1033">
                      Use high-quality logo files:
                    </span>
                    <span className="text-gray-600" data-id="element-1034">
                      {' '}
                      Upload a high-resolution, transparent PNG for the best
                      appearance across all reports and screen sizes.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1035">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1036" />
                  <div data-id="element-1037">
                    <span className="font-semibold text-gray-900" data-id="element-1038">
                      Match your brand colors exactly:
                    </span>
                    <span className="text-gray-600" data-id="element-1039">
                      {' '}
                      Use your brand's exact hex code for consistency with your
                      other marketing materials and website.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1040">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1041" />
                  <div data-id="element-1042">
                    <span className="font-semibold text-gray-900" data-id="element-1043">
                      Keep footer text concise:
                    </span>
                    <span className="text-gray-600" data-id="element-1044">
                      {' '}
                      A simple "Powered by [Agency Name]" or "Generated by
                      [Agency Name]" works best. Avoid long paragraphs.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1045">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1046" />
                  <div data-id="element-1047">
                    <span className="font-semibold text-gray-900" data-id="element-1048">
                      Test exports before sending to clients:
                    </span>
                    <span className="text-gray-600" data-id="element-1049">
                      {' '}
                      Generate sample reports to verify your branding appears
                      correctly before sharing with clients.
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1050">
                  <CheckCircle className="h-6 w-6 text-success mr-3 flex-shrink-0 mt-1" data-id="element-1051" />
                  <div data-id="element-1052">
                    <span className="font-semibold text-gray-900" data-id="element-1053">
                      Update branding as your agency evolves:
                    </span>
                    <span className="text-gray-600" data-id="element-1054">
                      {' '}
                      If you rebrand, update your Flowsense branding settings
                      immediately to maintain consistency.
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </section>

          {/* Related Guides */}
          <section className="border-t border-gray-200 pt-12" data-id="element-1055">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1056">
              Related Guides
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1057">
              <Link to="/guides/understanding-flow-health-scores" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1058">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1059">
                  Understanding Flow Health Scores
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1060">
                  Learn how Flowsense calculates health scores and what each
                  metric means for your flows.
                </p>
                <span className="text-indigo-600 font-medium" data-id="element-1061">
                  Read Guide →
                </span>
              </Link>
              <Link to="/guides/implementing-ai-recommendations" className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1062">
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1063">
                  Implementing AI Recommendations
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1064">
                  Learn how to prioritize and implement Flowsense
                  recommendations for maximum ROI.
                </p>
                <span className="text-indigo-600 font-medium" data-id="element-1065">
                  Read Guide →
                </span>
              </Link>
            </div>
          </section>

          {/* CTA */}
          <div className="mt-16 bg-gray-900 rounded-2xl p-8 lg:p-12 text-center" data-id="element-1066">
            <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1067">
              Ready to White-Label Flowsense?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto" data-id="element-1068">
              Start delivering professional, branded flow optimization reports
              to your agency clients today.
            </p>
            <a href="https://go.useflowsense.com/signup" data-id="element-1069">
              <Button size="lg" data-id="element-1070">Get Started Free</Button>
            </a>
          </div>
        </Container>
      </article>
    </div>;
}